'use client'

import type { ReactNode } from 'react'

/**
 * Diagrams for the Arabic article (PIX-188, /ar/blog/ai-answers-business-
 * visibility). Arabic copy, laid out with logical properties so they follow
 * <html dir="rtl">. Theming follows the blog diagram convention
 * (AILeadQualificationDiagrams): bg-muted/50 shell, bg-background cards,
 * border-border/60, theme text tokens; brand colours only on icons, borders
 * and tints, never as small-text colour. Pink is kept for urgency and
 * negative trends (blog skill colour table), so no signal here uses it.
 */

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          {title}
        </h3>
        {subtitle && (
          <p className='text-sm text-muted-foreground'>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  )
}

/** A Latin run inside Arabic text, isolated so bidi cannot reorder it. */
const L = ({ children }: { children: ReactNode }) => (
  <bdi dir='ltr'>{children}</bdi>
)

const ICONS: Record<
  'mention' | 'citation' | 'recommend' | 'accuracy',
  ReactNode
> = {
  mention: <path d='M4 5h16v10H9l-5 4V5z' strokeLinejoin='round' />,
  citation: (
    <>
      <path d='M10 14a4 4 0 0 0 6 0l3-3a4 4 0 0 0-6-6l-1 1' />
      <path d='M14 10a4 4 0 0 0-6 0l-3 3a4 4 0 0 0 6 6l1-1' />
    </>
  ),
  recommend: (
    <path
      d='M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6-4.5-4.2 6.1-.7L12 3z'
      strokeLinejoin='round'
    />
  ),
  accuracy: (
    <>
      <circle cx='12' cy='12' r='8' />
      <circle cx='12' cy='12' r='4' />
      <circle cx='12' cy='12' r='1' fill='currentColor' />
    </>
  ),
}

const SIGNALS = [
  {
    icon: 'mention' as const,
    color: '#008dcc',
    name: 'الذكر',
    is: 'ظهر اسم شركتك في نص الإجابة.',
    isNot: 'ليس توصية، وقد يأتي بوصف خاطئ.',
  },
  {
    icon: 'citation' as const,
    color: '#8b5cf6',
    name: 'الاستشهاد',
    is: 'أدرجت الإجابة رابطًا إلى صفحة من موقعك.',
    isNot: 'لا يثبت أن الإجابة نقلت الصفحة بدقة.',
  },
  {
    icon: 'recommend' as const,
    color: '#3CC29E',
    name: 'التوصية',
    is: 'قدّمت الإجابة شركتك كخيار مناسب للسائل.',
    isNot: 'ليست حصة سوقية ولا تتكرر بالضرورة.',
  },
  {
    icon: 'accuracy' as const,
    color: '#FDC304',
    name: 'دقة الوصف',
    is: 'الخدمات والسوق والحقائق صحيحة.',
    isNot: 'لا تقيسها الإشارات الثلاث الأخرى.',
  },
]

/** The four signals an AI answer can carry about a business. */
export function FourSignalsDiagram() {
  return (
    <Shell
      title='أربع إشارات في إجابة واحدة'
      subtitle='كل إشارة تجيب عن سؤال مختلف، فلا تجمعها في رقم واحد'
    >
      <div className='grid gap-4 sm:grid-cols-2'>
        {SIGNALS.map((s, i) => (
          <div
            key={s.name}
            className='p-5 rounded-lg bg-background border border-border/60'
            style={{ borderTopColor: s.color, borderTopWidth: 3 }}
          >
            <div className='flex items-center gap-3 mb-3'>
              <span
                className='grid size-10 shrink-0 place-items-center rounded-full'
                style={{ backgroundColor: `${s.color}1a`, color: s.color }}
              >
                <svg
                  viewBox='0 0 24 24'
                  width='20'
                  height='20'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.8'
                  aria-hidden='true'
                >
                  {ICONS[s.icon]}
                </svg>
              </span>
              <span className='text-base font-bold text-foreground leading-none'>
                {i + 1}. {s.name}
              </span>
            </div>
            <p className='text-sm text-foreground/80 mb-2'>
              <span className='font-semibold text-foreground'>يعني: </span>
              {s.is}
            </p>
            <p className='text-sm text-muted-foreground'>
              <span className='font-semibold'>حدود هذه الإشارة: </span>
              {s.isNot}
            </p>
          </div>
        ))}
      </div>
    </Shell>
  )
}

type Outcome = 'cited' | 'none' | 'absent' | 'partial' | 'inconclusive'

const OUTCOME: Record<
  Outcome,
  { label: string; color: string; glyph: ReactNode }
> = {
  cited: {
    label: 'ذكرنا واستشهد بصفحتنا',
    color: '#008dcc',
    glyph: <path d='M5 12.5l4.5 4.5L19 7.5' />,
  },
  none: {
    label: 'لم يذكرنا',
    color: '#9a958a',
    glyph: <path d='M6 12h12' />,
  },
  absent: {
    label: 'لم يذكرنا في النص الذي وصلنا',
    color: '#9a958a',
    glyph: <path d='M6 12h12' />,
  },
  partial: {
    label: 'لم يذكرنا في النص الذي وصلنا (نص منقطع)',
    color: '#8b5cf6',
    glyph: <path d='M6 12h3M11 12h2M15 12h3' />,
  },
  inconclusive: {
    label: 'غير حاسم: انقطع النص في جملته الأولى',
    color: '#FDC304',
    glyph: (
      <>
        <path d='M12 7v6' />
        <circle cx='12' cy='16.5' r='0.6' fill='currentColor' />
      </>
    ),
  },
}

const MATRIX: { model: string; ar: Outcome; en: Outcome }[] = [
  { model: 'Perplexity', ar: 'cited', en: 'partial' },
  { model: 'OpenAI', ar: 'none', en: 'none' },
  { model: 'Claude', ar: 'none', en: 'none' },
  { model: 'Gemini', ar: 'absent', en: 'inconclusive' },
]

function Cell({ outcome }: { outcome: Outcome }) {
  const o = OUTCOME[outcome]
  return (
    <div className='flex h-full items-center gap-2 p-3 rounded-lg bg-background border border-border/60'>
      <span
        className='grid size-7 shrink-0 place-items-center rounded-full'
        style={{ backgroundColor: `${o.color}26`, color: o.color }}
      >
        <svg
          viewBox='0 0 24 24'
          width='16'
          height='16'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.4'
          strokeLinecap='round'
          aria-hidden='true'
        >
          {o.glyph}
        </svg>
      </span>
      <span className='text-xs text-foreground leading-snug'>{o.label}</span>
    </div>
  )
}

/**
 * The discovery question's results, one run per model and language,
 * 23 Oct 2026 (raw answers attached to PIX-188).
 */
export function ArabicEnglishResultsMatrix() {
  return (
    <Shell
      title='سؤال الاكتشاف بالعربية والإنجليزية: هل ذُكرت علامتنا؟'
      subtitle='اختبار على علامتنا، 23 أكتوبر 2026، إجابة واحدة لكل نموذج ولغة عبر API'
    >
      {/* A native table, so the model and language headers are announced
          with each outcome; the cells keep the card look. */}
      <table className='w-full table-fixed border-separate border-spacing-2'>
        <caption className='sr-only'>
          هل ذكرت كل إجابة علامتنا، حسب النموذج ولغة السؤال
        </caption>
        <thead>
          <tr>
            <th
              scope='col'
              className='w-24 text-start align-bottom text-xs font-semibold text-muted-foreground'
            >
              النموذج
            </th>
            <th
              scope='col'
              className='text-start align-bottom text-xs font-semibold text-muted-foreground'
            >
              السؤال بالعربية
            </th>
            <th
              scope='col'
              className='text-start align-bottom text-xs font-semibold text-muted-foreground'
            >
              السؤال بالإنجليزية
            </th>
          </tr>
        </thead>
        <tbody>
          {MATRIX.map(row => (
            <tr key={row.model}>
              <th
                scope='row'
                className='text-start align-middle text-sm font-bold text-foreground'
              >
                <L>{row.model}</L>
              </th>
              <td className='h-px align-top'>
                <Cell outcome={row.ar} />
              </td>
              <td className='h-px align-top'>
                <Cell outcome={row.en} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className='text-xs text-muted-foreground mt-4 text-center'>
        <L>OpenAI</L> (<L>gpt-6-luna</L>) و<L>Claude</L> عبر <L>API</L> دون بحث
        في الويب؛ <L>Perplexity</L> و<L>Gemini</L> مع البحث في الويب. عيّنة
        واحدة لا تصلح للحكم على أي نموذج.
      </p>
    </Shell>
  )
}
